import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";

/**
 * Custom hook to handle idle cart deletion for TouchBased mode
 * Shows warning after 40 seconds of inactivity, deletes after 50 seconds total
 */
export const useIdleCartDeletion = ({
  isTouchBased,
  cartLength,
  videoAds,
  onShowCartWarning,
  onHideCartWarning,
  onShowVideoAd,
}) => {
  const dispatch = useDispatch();
  const idleTimeoutRef = useRef(null);
  const warningTimeoutRef = useRef(null);

  const resetIdleTimer = () => {
    // Clear existing timers
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);

    // Close warning modal
    onHideCartWarning();

    if (isTouchBased) {
      if (cartLength > 0) {
        // Cart not empty: Show warning after 40 seconds
        idleTimeoutRef.current = setTimeout(() => {
          onShowCartWarning();

          // Delete cart after 10 more seconds (50 seconds total)
          warningTimeoutRef.current = setTimeout(() => {
            // Try to clear cart via Redux
            try {
              const { clearCart } = require("../../../../slices/cart/publicOrderingCartSlice");
              dispatch(clearCart());
            } catch (err) {
              console.warn("⚠️ Could not dispatch clearCart action:", err.message);
            }

            try {
              localStorage.removeItem("publicOrderingCart");
            } catch (err) {
              console.error("Error clearing cart from localStorage:", err);
            }
            onHideCartWarning();
            
            // Show video ad after cart deletion
            setTimeout(() => {
              if (videoAds.length > 0) {
                console.log("🎥 Showing video ad after idle timeout");
                onShowVideoAd();
              }
            }, 300);
          }, 10000);
        }, 40000);
      } else {
        // Cart empty: Show video ad after 40 seconds
        idleTimeoutRef.current = setTimeout(() => {
          if (videoAds.length > 0) {
            console.log("🎥 Showing video ad (empty cart idle timeout)");
            onShowVideoAd();
          }
        }, 40000);
      }
    }
  };

  // Track user interactions
  useEffect(() => {
    if (!isTouchBased) return;

    const events = ["click", "keydown", "scroll", "touchstart", "mousedown"];
    const handleInteraction = () => resetIdleTimer();

    events.forEach((event) => {
      document.addEventListener(event, handleInteraction);
    });

    // Initial timer setup
    resetIdleTimer();

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleInteraction);
      });
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, [cartLength, isTouchBased, videoAds.length]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
    };
  }, []);

  // Return refs for access in component if needed
  return { idleTimeoutRef, warningTimeoutRef };
};
